import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";
import { AccessService } from "src/app/core/services/access.service";
import { environment as env } from "src/environments/environment";
import { GenericResponse } from "src/app/shared/interfaces/generic.interface";
import { Approver, DataScheduleByUser, RequestDetail, RequestList, cancelRequestData, footerList } from "../interfaces/interfaces";
import { TableService } from "src/app/shared/helpers/list-table";
import { footerTransform, transforDataRequestList } from "../helpers/mapDataRequestList";
import { GenericResponseList } from "src/app/shared/interfaces/table.interface";
import { catchError, map } from 'rxjs/operators';
import { REFRESH_INDEXED } from "src/app/core/indexedDB/helpers/enums.enums";
import { IndexedDBDexieService } from "src/app/core/indexedDB/service/indexed-db-dexie.service";
import { localDB } from "src/app/shared/helpers/localStorage";
import { ConfigurationsService } from "src/app/shared/services/configurations.service";
import { RequestExtempPayload } from "src/app/core/layout/components/trademark-regularization/interface/interfaces";
import { ClosureDataToSent } from "src/app/modules/payroll/interfaces/request-closing-historic";

export interface CreateRequestFnPayload {
  colaboradoresId: number[];
  nId_Tipo_Solicitud: number;
  dFecha_Inicio: string;       // ISO date: "2026-05-20"
  dHora_Inicio?: string;       // "HH:mm:ss", opcional
  nCantidad: number;
  nId_Tipo_Motivo: number;
  sMotivo?: string;
  isExtemp?: boolean;
  nId_Tarea?: number;
  sReplacementCollaborator?: string;
  deviceInfo?: {
    sBrowser: string; sUid: string; nWidth: number; nHeight: number;
    sOS: string; bEmulated: number; nMethod: number;
    latitude?: number; longitude?: number; sIp?: string;
  };
  aExtemp?: { dTiempo_Marca?: string; nInterval?: number; nId_Mark?: number; nType_Mark: number; nAccion: number; sMotivo?: string }[];
  files?: File[];
}

@Injectable({
  providedIn: "root",
})

// implements DefaultService
export class RequestsService implements TableService {
  requestCard: any = {
    isSuccess: true,
    code: 200,
    message: 'init',
    data: []
  }
  requestCardSubjet = new BehaviorSubject<any>(this.requestCard)
  getLastRequest = this.requestCardSubjet.asObservable()

  constructor(
    private readonly http: HttpClient,
    private readonly _accessService: AccessService,
    private readonly _indexDB: IndexedDBDexieService,
    private readonly _ConfigService: ConfigurationsService
  ) { }

  getAll(
    records: number,
    sort: string,
    order: string,
    numPage: number,
    getInputs = ""
  ): Observable<GenericResponseList<RequestList>> {
    const requestUrl = `${env.api_url}api/solicitud/listar?records=${records}&sort=${sort}&order=${order}&numPage=${numPage + 1}${getInputs}`;
    return forkJoin([
      this.http.get<GenericResponseList<RequestList>>(requestUrl),
      this._ConfigService.getConfig(REFRESH_INDEXED.EVERY_REFRESH, ['proyectos', 'todo_requerimiento', 'categorias_all', 'lideres_directores_all', 'tipo_hora', 'tipo_actividad', 'tipo_solicitud'])
    ]).pipe(
      map((response) => {
        return transforDataRequestList(response[0], this._accessService, response[1]);
      })
    );
  }

  getFooter(getInputs: string): Observable<GenericResponse<footerList>> {
    const requestUrl = `${env.api_url}api/solicitud/listar_total_general?${getInputs}`;
    return this.http.get<GenericResponse<footerList>>(requestUrl).pipe(
      map(response => {
        return footerTransform(response);
      })
    );
  }

  requestRegister(data: any): Observable<GenericResponse<string>> {
    const endpoint = env.api_url + "api/solicitud/crear";
    const headers = new HttpHeaders();
    headers.set("Content-Type", "multipart/form-data; charset=utf-8");
    return this.http.post<GenericResponse<string>>(endpoint, data, { headers: headers });
  }

  requestExtemp(data: RequestExtempPayload): Observable<GenericResponse<RequestExtempPayload>> {
    const endpoint = env.api_url + "api/marcas/crear-extemp";
    return this.http.post<GenericResponse<RequestExtempPayload>>(endpoint, data);
  }

  // requestMassiveRegister(data: any): Observable<GenericResponse<string>> {
  //   const endpoint = env.api_url + "api/solicitud/masivo/crear";
  //   const headers = new HttpHeaders();
  //   headers.set("Content-Type", "multipart/form-data; charset=utf-8");
  //   return this.http.post<GenericResponse<string>>(endpoint, data, { headers: headers });
  // }

requestMassiveRegister(
  data: CreateRequestFnPayload,
  files?: File[]
): Observable<GenericResponse<any>> {

  const endpoint = env.api_url + 'api/solicitud/massive/create';

  const formData = new FormData();

  data.colaboradoresId.forEach(x => {
    formData.append('colaboradoresId', x.toString());
  });

  formData.append('nId_Tipo_Solicitud', data.nId_Tipo_Solicitud.toString());
  formData.append('dFecha_Inicio', data.dFecha_Inicio);
  
  if (data.dHora_Inicio)
    formData.append('dHora_Inicio', data.dHora_Inicio);

  formData.append('nCantidad', data.nCantidad.toString());
  formData.append('nId_Tipo_Motivo', data.nId_Tipo_Motivo.toString());

  if (data.sMotivo)
    formData.append('sMotivo', data.sMotivo);

  formData.append('isExtemp', data.isExtemp ? data.isExtemp.toString() : 'false');

  if (data.nId_Tarea)
    formData.append('nId_Tarea', data.nId_Tarea.toString());

  if (data.sReplacementCollaborator)
    formData.append('sReplacementCollaborator', data.sReplacementCollaborator);

  if (data.deviceInfo)
    formData.append('deviceInfo', JSON.stringify(data.deviceInfo));

  if (data.aExtemp)
    formData.append('aExtemp', JSON.stringify(data.aExtemp));

  files?.forEach(file => {
    formData.append('files', file);
  });

  return this.http.post<GenericResponse<any>>(endpoint, formData);
}

  requestLastRequest() {
    const endpoint = env.api_url + 'api/solicitud';
    return this.http.get<any>(endpoint).pipe(
      map(resp => {

        localDB.setItem('c-requests', resp.data)
        this.requestCardSubjet.next(resp)
      }),
      catchError(err => {
        console.log(err)
        throw err
      })
    )
  }

  changeStateRequest(nId: number, type: number, data: cancelRequestData, canApproveOrRefuseAnyRequest: boolean = false): Observable<GenericResponse<RequestDetail>> {
    let dataForm: FormData = this.setFormData(data)
    const api = `${env.api_url}api/solicitud/${(canApproveOrRefuseAnyRequest) ? 'aprobarRechazarLiderAdm' : 'aprobarRechazarnew'}?nId_Soli=${nId}`
    const headers = new HttpHeaders();
    headers.set("Content-Type", "multipart/form-data; charset=utf-8");
    return this.http
      .post<GenericResponse<RequestDetail>>(
        api, dataForm, { headers: headers }
      );
  }

  private setFormData(data: cancelRequestData) {
    let formData: FormData = new FormData();
    for (let clave in data) {
      switch (clave) {
        case 'aFiles':
          data[clave].forEach((file: File) => {
            formData.append("aFiles", file);
          });
          break
        default:
          //@ts-ignore
          formData.append(clave, data[clave]);
          break
      }
    }

    return formData;
  }

  getRequestDetail(idRequest: number): Observable<GenericResponse<RequestDetail>> {
    return this.http.get<any>(`${env.api_url}api/solicitud/detalle-solicitud?nId_Solicitud=${idRequest}`);
  }

  getScheduleByUser(idUsuario: number): Observable<any> {
    let nId_Usuario = localDB.getItem('idUsuario');
    if (nId_Usuario === idUsuario.toString()) {
      return this._indexDB.getRequestData<GenericResponse<DataScheduleByUser>>('api/horario/obtenerHorario?nId_User=', 'endpoints', REFRESH_INDEXED.EVERY_DAY, { query: idUsuario.toString() });
    } else {
      const request = env.api_url + 'api/horario/obtenerHorario?nId_User=' + idUsuario.toString();
      return this.http.get<any>(request).pipe(
        catchError((err: any) => {
          console.log(err);
          return of(null);
        })
      )
    }
  }

  getApprovers(): Observable<GenericResponse<Approver>> {
    return this._indexDB.getRequestData<GenericResponse<Approver>>('api/solicitud/futuro-aprobador', 'endpoints', REFRESH_INDEXED.EVERY_DAY)
  }

  cancelRequest(nId: number, data: cancelRequestData): Observable<GenericResponse<string>> {
    return this.http
      .put<GenericResponse<string>>(
        env.api_url + "api/solicitud/anularSolicitud?nId_Solicitud=" + nId,
        data
      );
  }

  generateClosure(closureData: ClosureDataToSent): Observable<any> {
    return this.http.post<any>(env.api_url + "api/solicitud/cerrar-solicitud", closureData);
  }

  getCollaboratorPeriods(nId_Collaborator: number): Observable<GenericResponse<any[]>> {
    const url = `${env.api_url}api/vacation/periods/${nId_Collaborator}`;
    return this.http.get<GenericResponse<any[]>>(url);
  }

}